import React, { useRef, useState } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Typography,
  Box,
  Chip,
  IconButton,
  useTheme,
  CircularProgress,
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  Restaurant as DifficultyIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Recipe } from '../models/Recipe';

interface EnhancedRecipeCardProps {
  recipe: Recipe;
  onClick: (recipeId: string) => void;
  isFavorite?: boolean;
  onFavoriteToggle?: (recipeId: string) => void | Promise<void>;
  showStats?: boolean;
}

const EnhancedRecipeCard: React.FC<EnhancedRecipeCardProps> = ({
  recipe,
  onClick,
  isFavorite = false,
  onFavoriteToggle,
  showStats = true,
}) => {
  const theme = useTheme();
  const cardRef = useRef<HTMLDivElement>(null);
  const [isFavoriting, setIsFavoriting] = useState(false);

  const ingredientCount = recipe.ingredients
    ? recipe.ingredients.split('\n').filter(ing => ing.trim()).length
    : 0;

  const estimatedTime = Math.max(15, Math.min(60, ingredientCount * 5));

  const difficulty = ingredientCount < 5 ? 'Easy' : ingredientCount < 10 ? 'Medium' : 'Hard';
  const difficultyColor = difficulty === 'Easy' ? 'success' : difficulty === 'Medium' ? 'warning' : 'error';

  // Parallax effect for image on mouse move
  const handleMouseMove = (e: React.MouseEvent) => {
    const card = cardRef.current;
    if (!card) { return; }
    const rect = card.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 10; // -5 to 5
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 10; // -5 to 5
    const img = card.querySelector('.recipe-image') as HTMLElement;
    if (img) { img.style.transform = `scale(1.05) translate(${x}px, ${y}px)`; }
  };

  const handleMouseLeave = () => {
    const card = cardRef.current;
    if (!card) { return; }
    const img = card.querySelector('.recipe-image') as HTMLElement;
    if (img) { img.style.transform = 'scale(1)'; }
  };

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onFavoriteToggle && !isFavoriting) {
      setIsFavoriting(true);
      try {
        await onFavoriteToggle(recipe.id);
      } catch (error) {
        console.error('Failed to toggle favorite:', error);
      } finally {
        setIsFavoriting(false);
      }
    }
  };

  return (
    <motion.div
      whileHover={{ y: -4, boxShadow: '0 0 0 4px rgba(255,107,53,0.18), 0 8px 32px rgba(0,0,0,0.12)' }}
      transition={{ duration: 0.2 }}
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ borderRadius: 16 }}
    >
      <Card 
        sx={{ 
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 4,
          boxShadow: '0px 4px 20px rgba(0,0,0,0.08)',
          border: '1.5px solid rgba(255,107,53,0.08)',
          transition: 'box-shadow 0.3s cubic-bezier(0.4,0,0.2,1), border-color 0.3s',
          '&:hover': {
            boxShadow: '0 0 0 6px rgba(255,107,53,0.22), 0 12px 40px rgba(0,0,0,0.16)',
            borderColor: 'rgba(255,107,53,0.22)',
            '& .recipe-overlay': {
              opacity: 0.7,
            },
          },
        }}
      >
        <CardActionArea
          onClick={() => onClick(recipe.id)}
          sx={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
          }}
        >
          {/* Image Section */}
          <Box sx={{ position: 'relative', overflow: 'hidden' }}>
            <CardMedia
              component="img"
              height="200"
              image={recipe.image || '/assets/images/placeholder.png'}
              alt={recipe.title}
              loading="lazy" // Native lazy loading
              className="recipe-image"
              sx={{
                transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
                objectFit: 'cover',
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
              }}
            />
            
            {/* Gradient Overlay */}
            <Box
              className="recipe-overlay"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(180deg, rgba(255,107,53,0.18) 0%, rgba(0,0,0,0.32) 100%)',
                opacity: 0.5,
                transition: 'opacity 0.3s cubic-bezier(0.4,0,0.2,1)',
                pointerEvents: 'none',
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
              }}
            />

            {/* Favorite Button */}
            {onFavoriteToggle && (
              <IconButton
                onClick={handleFavoriteClick}
                disabled={isFavoriting}
                sx={{
                  position: 'absolute',
                  top: { xs: 8, md: 12 },
                  right: { xs: 8, md: 12 },
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(4px)',
                  minWidth: { xs: 44, md: 40 },
                  minHeight: { xs: 44, md: 40 },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                  },
                }}
                size="medium"
              >
                {isFavoriting ? (
                  <CircularProgress size={20} />
                ) : isFavorite ? (
                  <FavoriteIcon sx={{ color: theme.palette.error.main }} />
                ) : (
                  <FavoriteBorderIcon />
                )}
              </IconButton>
            )}

            {/* Quick Stats */}
            {showStats && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 12,
                  left: 12,
                  display: 'flex',
                  gap: 1,
                }}
              >
                <Chip
                  icon={<TimeIcon sx={{ fontSize: '16px !important' }} />}
                  label={`${estimatedTime}m`}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(4px)',
                    fontSize: '0.75rem',
                    height: 24,
                  }}
                />
                <Chip
                  icon={<DifficultyIcon sx={{ fontSize: '16px !important' }} />}
                  label={difficulty}
                  size="small"
                  color={difficultyColor}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(4px)',
                    fontSize: '0.75rem',
                    height: 24,
                  }}
                />
                {/* Diet Chip (future use, not in Recipe model) */}
                {/* {recipe.diet && (
                  <Chip
                    icon={<DietIcon sx={{ fontSize: '16px !important' }} />}
                    label={recipe.diet}
                    size="small"
                    color="info"
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(4px)',
                      fontSize: '0.75rem',
                      height: 24,
                    }}
                  />
                )} */}
                {/* GreenTokens Badge (future use, not in Recipe model) */}
                {/* {recipe.greenTokens && (
                  <Tooltip title={`Earn ${recipe.greenTokens} GreenTokens`}>
                    <Chip
                      icon={<TokenIcon sx={{ fontSize: '16px !important', color: '#4A7C59' }} />}
                      label={recipe.greenTokens}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(74,124,89,0.12)',
                        color: '#4A7C59',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 24,
                        border: '1px solid #4A7C59',
                        boxShadow: '0 0 8px rgba(74,124,89,0.18)',
                      }}
                    />
                  </Tooltip>
                )} */}
              </Box>
            )}
          </Box>

          {/* Content Section */}
          <CardContent sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
            <Typography
              variant="h6"
              component="h3"
              gutterBottom
              sx={{
                fontWeight: 600,
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                minHeight: '2.6em',
                fontSize: { xs: '1rem', md: '1.125rem' },
              }}
            >
              {recipe.title}
            </Typography>
            
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                mb: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                minHeight: '2.5em',
              }}
            >
              {recipe.notes || 'No description available'}
            </Typography>

            {/* Ingredients Count */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">
                {ingredientCount} {ingredientCount === 1 ? 'ingredient' : 'ingredients'}
              </Typography>
              
              {recipe.steps && recipe.steps.length > 0 && (
                <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                  {recipe.steps.length} steps
                </Typography>
              )}
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
    </motion.div>
  );
};

export default React.memo(EnhancedRecipeCard, (prevProps, nextProps) => {
  return (
    prevProps.recipe.id === nextProps.recipe.id &&
    prevProps.isFavorite === nextProps.isFavorite &&
    prevProps.showStats === nextProps.showStats &&
    prevProps.recipe.title === nextProps.recipe.title &&
    prevProps.recipe.image === nextProps.recipe.image &&
    prevProps.recipe.ingredients === nextProps.recipe.ingredients
  );
});
